import React, { useState, useEffect, useMemo } from 'react';
import { Briefcase, FileText, Users, TrendingUp } from 'lucide-react';
import { getValuesFromLocalstorage } from '../../../components/Helpers';
import LOCAL_KEYS from '../../../helpers/localstorage';
import { getDashboardData } from '../../../Services/recruiterapis';
import Loader from '../../../components/Loader';
import { toast } from 'react-toastify';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';

const RecruiterDashboard = () => {
  const [stats, setStats] = useState({
    totalRequirements: 0,
    activeRequirements: 0,
    closedRequirements: 0,
    totalCandidates: 0,
    activeCandidates: 0,
    closedCandidates: 0,
  });
  const [isLoading, setIsLoading] = useState(true);

  const userDetails = getValuesFromLocalstorage(LOCAL_KEYS.USER_DETAILS);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setIsLoading(true);
        const data = await getDashboardData();
        setStats({
          totalRequirements: data.totalRequirements || 0,
          activeRequirements: data.activeRequirements || 0,
          closedRequirements: data.closedRequirements || 0,
          totalCandidates: data.totalCandidates || 0,
          activeCandidates: data.activeCandidates || 0,
          closedCandidates: data.closedCandidates || 0,
        });
      } catch (error) {
        console.error('Failed to fetch dashboard data:', error);
        toast.error('Failed to load dashboard data');
      } finally {
        setIsLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  // Color scheme matching EmployeeDashboard
  const statusColors = {
    open: {
      light: "rgba(16, 185, 129, 0.8)", // Green for open
      main: "rgba(16, 185, 129, 0.9)",
    },
    closed: {
      light: "rgba(107, 114, 128, 0.8)", // Gray for closed
      main: "rgba(107, 114, 128, 0.9)",
    },
  };

  // Different color scheme for Candidates chart
  const candidateColors = {
    active: {
      light: "rgba(59, 130, 246, 0.8)", // Blue for active candidates
      main: "rgba(59, 130, 246, 0.9)",
    },
    inactive: {
      light: "rgba(239, 68, 68, 0.8)", // Red for closed candidates
      main: "rgba(239, 68, 68, 0.9)",
    },
  };

  // Prepare data for Requirements Pie Chart
  const requirementsData = useMemo(() => [
    { name: 'Open', value: stats.activeRequirements, color: statusColors.open.light },
    { name: 'Closed', value: stats.closedRequirements, color: statusColors.closed.light },
  ], [stats.activeRequirements, stats.closedRequirements]);

  // Prepare data for Candidates Pie Chart
  const candidatesData = useMemo(() => [
    { name: 'Active', value: stats.activeCandidates, color: candidateColors.active.light },
    { name: 'Inactive', value: stats.closedCandidates, color: candidateColors.inactive.light },
  ], [stats.activeCandidates, stats.closedCandidates]);


  return (
    <div className="bg-gray-50">
      <Loader isLoading={isLoading} />
      <div className="h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div className="mb-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 mb-1">
                Welcome back, {userDetails?.firstName || 'Recruiter'}!
              </h1>
             
            </div>
           
          </div>
        </div>

        {/* Pie Charts */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          {/* Requirements Pie Chart */}
          <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                <Briefcase className="w-5 h-5 mr-2 text-blue-600" />
                Requirements Overview
              </h2>
              <div className="text-right">
                <p className="text-2xl font-bold text-gray-900">{stats.totalRequirements}</p>
                <p className="text-xs text-gray-500">Total</p>
              </div>
            </div>
            <div className="h-64 flex flex-col items-center justify-center">
              <div className="h-56 w-56 relative">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={requirementsData}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={95}
                      fill="#8884d8"
                      paddingAngle={5}
                      dataKey="value"
                    >
                      {requirementsData.map((entry, index) => (
                        <Cell 
                          key={`cell-${index}`} 
                          fill={entry.color}
                          style={{ cursor: 'pointer' }}
                        />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{
                        backgroundColor: "#fff",
                        borderRadius: "0.5rem",
                        boxShadow:
                          "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
                        border: "none",
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="mt-4 flex flex-wrap justify-center gap-4">
                {requirementsData.map((item, index) => (
                  <div 
                    key={index} 
                    className="flex items-center cursor-pointer"
                  >
                    <div
                      className="w-3 h-3 rounded-full mr-2"
                      style={{ backgroundColor: item.color }}
                    ></div>
                    <span className="text-sm text-gray-700">
                      {item.name}: {item.value}
                    </span>
                  </div>
                ))}
              </div>
            </div>
           
          </div>

          {/* Candidates Pie Chart */}
          <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                <Users className="w-5 h-5 mr-2 text-purple-600" />
                Candidates Overview
              </h2>
              <div className="text-right">
                <p className="text-2xl font-bold text-gray-900">{stats.totalCandidates}</p>
                <p className="text-xs text-gray-500">Total</p>
              </div>
            </div>
            <div className="h-64 flex flex-col items-center justify-center">
              <div className="h-56 w-56 relative">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={candidatesData}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={95}
                      fill="#8884d8"
                      paddingAngle={5}
                      dataKey="value"
                    >
                      {candidatesData.map((entry, index) => (
                        <Cell 
                          key={`cell-${index}`} 
                          fill={entry.color}
                          style={{ cursor: 'pointer' }}
                        />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{
                        backgroundColor: "#fff",
                        borderRadius: "0.5rem",
                        boxShadow:
                          "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
                        border: "none",
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="mt-4 flex flex-wrap justify-center gap-4">
                {candidatesData.map((item, index) => (
                  <div 
                    key={index} 
                    className="flex items-center cursor-pointer"
                  >
                    <div
                      className="w-3 h-3 rounded-full mr-2"
                      style={{ backgroundColor: item.color }}
                    ></div>
                    <span className="text-sm text-gray-700">
                      {item.name}: {item.value}
                    </span>
                  </div>
                ))}
              </div>
            </div>
         
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-xl font-semibold text-gray-900">Quick Actions</h2>
            <span className="text-xs font-medium text-gray-500 bg-gray-50 px-3 py-1.5 rounded-full">
              Quick Access
            </span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <a
              href="/requirements"
              className="group flex items-center p-5 bg-gradient-to-br from-blue-50 via-blue-50/80 to-blue-100/50 hover:from-blue-100 hover:via-blue-100/90 hover:to-blue-200/60 rounded-xl transition-all duration-300 border border-blue-200/60 hover:border-blue-300 hover:shadow-md"
            >
              <div className="flex-shrink-0 w-11 h-11 bg-blue-600 rounded-xl flex items-center justify-center mr-4 group-hover:bg-blue-700 group-hover:scale-110 transition-all duration-300 shadow-sm">
                <Briefcase className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-gray-900 mb-1 group-hover:text-blue-700 transition-colors">
                  Manage Requirements
                </p>
                <p className="text-sm text-gray-600 leading-snug">
                  View and manage all recruitment requirements
                </p>
              </div>
              <svg className="w-5 h-5 text-blue-600 opacity-0 group-hover:opacity-100 transform translate-x-0 group-hover:translate-x-1 transition-all duration-300 ml-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </a>
            <a
              href="/candidates"
              className="group flex items-center p-5 bg-gradient-to-br from-indigo-50 via-indigo-50/80 to-indigo-100/50 hover:from-indigo-100 hover:via-indigo-100/90 hover:to-indigo-200/60 rounded-xl transition-all duration-300 border border-indigo-200/60 hover:border-indigo-300 hover:shadow-md"
            >
              <div className="flex-shrink-0 w-11 h-11 bg-indigo-600 rounded-xl flex items-center justify-center mr-4 group-hover:bg-indigo-700 group-hover:scale-110 transition-all duration-300 shadow-sm">
                <Users className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-gray-900 mb-1 group-hover:text-indigo-700 transition-colors">
                  Candidates
                </p>
                <p className="text-sm text-gray-600 leading-snug">
                  View available candidates
                </p>
              </div>
              <svg className="w-5 h-5 text-indigo-600 opacity-0 group-hover:opacity-100 transform translate-x-0 group-hover:translate-x-1 transition-all duration-300 ml-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </a>
          </div>
        </div>

       
      </div>
    </div>
  );
};

export default RecruiterDashboard;

